import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Search, Filter, Activity, Users, Clock, AlertTriangle, Globe, MousePointer, ChevronDown, ChevronRight, Copy, Monitor, Wifi, WifiOff } from 'lucide-react';
import { HelpTooltip } from '@/components/help/HelpTooltip';

// Tab definitions for quick log_type filtering
const TABS = [
  { key: '', label: 'All', icon: Activity },
  { key: 'business_action', label: 'User Actions', icon: Users },
  { key: 'http_request', label: 'API Requests', icon: Globe },
  { key: 'frontend_event', label: 'Frontend Events', icon: MousePointer },
  { key: 'backend_error', label: 'Backend Errors', icon: AlertTriangle },
];

// ---- Detail Expansion Components ----

function DetailSection({ title, children }) {
  if (!children) return null;
  return (
    <div className="mb-3">
      <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{title}</div>
      {children}
    </div>
  );
}

function DetailField({ label, value, mono, copyable }) {
  if (!value && value !== 0) return null;
  const displayVal = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
  if (!displayVal || displayVal === '""' || displayVal === '{}' || displayVal === '[]') return null;

  return (
    <div className="flex gap-2 mb-0.5 items-start">
      <span className="text-[11px] text-gray-400 min-w-[100px] flex-shrink-0">{label}:</span>
      <span className={`text-[11px] text-gray-700 break-all ${mono ? 'font-mono bg-gray-50 px-1 rounded' : ''}`}>
        {displayVal.length > 200 ? (
          <pre className="whitespace-pre-wrap text-[10px] bg-gray-50 p-2 rounded max-h-40 overflow-auto">{displayVal}</pre>
        ) : displayVal}
      </span>
      {copyable && displayVal.length > 20 && (
        <button
          onClick={() => navigator.clipboard?.writeText(displayVal)}
          className="text-gray-300 hover:text-gray-500 flex-shrink-0"
          title="Copy"
        >
          <Copy className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function BreadcrumbTrail({ breadcrumbs }) {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;
  return (
    <DetailSection title="Breadcrumb Trail (actions before error)">
      <div className="bg-gray-50 rounded p-2 max-h-32 overflow-auto">
        {breadcrumbs.map((b, i) => (
          <div key={i} className="text-[10px] text-gray-500 flex gap-2">
            <span className="text-gray-300 w-3 text-right flex-shrink-0">{i + 1}</span>
            <Badge variant="outline" className="text-[9px] px-1 py-0">{b.type}</Badge>
            <span className="truncate">{b.summary}</span>
            <span className="text-gray-300 ml-auto flex-shrink-0">
              {new Date(b.ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kolkata' })}
            </span>
          </div>
        ))}
      </div>
    </DetailSection>
  );
}

function BrowserInfo({ browser }) {
  if (!browser) return null;
  return (
    <DetailSection title="Browser / Device">
      <div className="flex flex-wrap gap-x-4 gap-y-0.5">
        <DetailField label="Browser" value={browser.browser} />
        <DetailField label="Platform" value={browser.platform} />
        <DetailField label="Viewport" value={`${browser.viewportWidth}x${browser.viewportHeight}`} />
        <DetailField label="Screen" value={`${browser.screenWidth}x${browser.screenHeight}`} />
        <DetailField label="Online" value={browser.online ? 'Yes' : 'No'} />
        <DetailField label="Language" value={browser.language} />
      </div>
      <DetailField label="User Agent" value={browser.userAgent} mono />
    </DetailSection>
  );
}

// Render expanded detail panel based on event type
function ExpandedDetails({ log }) {
  const d = log.details || {};

  // API Error — richest detail
  if (log.event_type === 'api_error') {
    return (
      <div className="p-3 bg-red-50/50 border-l-2 border-red-300">
        <DetailSection title="Request">
          <DetailField label="Method" value={d.method} />
          <DetailField label="URL" value={d.fullUrl || d.url} mono copyable />
          <DetailField label="Params" value={d.requestParams} mono />
          <DetailField label="Body" value={d.requestBody} mono copyable />
          <DetailField label="Headers" value={d.requestHeaders} mono />
          <DetailField label="Timeout" value={d.timeout ? `${d.timeout}ms` : null} />
        </DetailSection>
        <DetailSection title="Response">
          <DetailField label="Status" value={d.status ? `${d.status} ${d.statusText}` : 'No response'} />
          <DetailField label="Body" value={d.responseBody} mono copyable />
          <DetailField label="Headers" value={d.responseHeaders} mono />
        </DetailSection>
        <DetailSection title="Error">
          <DetailField label="Message" value={d.message} />
          <DetailField label="Code" value={d.errorCode} mono />
          <DetailField label="Name" value={d.errorName} />
          <DetailField label="Duration" value={d.durationMs ? `${d.durationMs}ms` : null} />
          <DetailField label="Network" value={d.online ? 'Online' : 'OFFLINE'} />
          <DetailField label="Has Response" value={d.hasResponse ? 'Yes (server replied)' : 'No (network/timeout)'} />
          <DetailField label="Stack" value={d.stack} mono copyable />
        </DetailSection>
        <BreadcrumbTrail breadcrumbs={log.breadcrumbs} />
        <BrowserInfo browser={log.browser} />
      </div>
    );
  }

  // JS Error
  if (log.event_type === 'js_error') {
    return (
      <div className="p-3 bg-red-50/50 border-l-2 border-red-300">
        <DetailSection title="Error">
          <DetailField label="Message" value={d.message} />
          <DetailField label="Name" value={d.errorName} />
          <DetailField label="Source" value={d.source} mono />
          <DetailField label="Line:Col" value={d.lineno ? `${d.lineno}:${d.colno}` : null} />
          <DetailField label="Stack" value={d.stack} mono copyable />
        </DetailSection>
        <DetailSection title="Context">
          <DetailField label="URL" value={d.currentUrl} mono />
          <DetailField label="Page Title" value={d.documentTitle} />
        </DetailSection>
        <BreadcrumbTrail breadcrumbs={log.breadcrumbs} />
        <BrowserInfo browser={log.browser} />
      </div>
    );
  }

  // React Error
  if (log.event_type === 'react_error') {
    return (
      <div className="p-3 bg-red-50/50 border-l-2 border-red-300">
        <DetailSection title="React Crash">
          <DetailField label="Message" value={d.message} />
          <DetailField label="Name" value={d.errorName} />
          <DetailField label="Stack" value={d.stack} mono copyable />
          <DetailField label="Component Stack" value={d.componentStack} mono copyable />
        </DetailSection>
        <BreadcrumbTrail breadcrumbs={log.breadcrumbs} />
        <BrowserInfo browser={log.browser} />
      </div>
    );
  }

  // Unhandled Rejection
  if (log.event_type === 'unhandled_rejection') {
    return (
      <div className="p-3 bg-orange-50/50 border-l-2 border-orange-300">
        <DetailSection title="Promise Rejection">
          <DetailField label="Message" value={d.message} />
          <DetailField label="Name" value={d.errorName} />
          <DetailField label="Code" value={d.errorCode} mono />
          <DetailField label="Stack" value={d.stack} mono copyable />
        </DetailSection>
        {d.apiUrl && (
          <DetailSection title="Related API Call">
            <DetailField label="Method" value={d.apiMethod} />
            <DetailField label="URL" value={d.apiUrl} mono />
            <DetailField label="Status" value={d.apiStatus} />
            <DetailField label="Response" value={d.apiResponseBody} mono />
          </DetailSection>
        )}
        <BreadcrumbTrail breadcrumbs={log.breadcrumbs} />
        <BrowserInfo browser={log.browser} />
      </div>
    );
  }

  // Console Error
  if (log.event_type === 'console_error') {
    return (
      <div className="p-3 bg-yellow-50/50 border-l-2 border-yellow-300">
        <DetailSection title="Console Error">
          <DetailField label="Message" value={d.message} mono copyable />
          <DetailField label="Args Count" value={d.argCount} />
        </DetailSection>
        <BrowserInfo browser={log.browser} />
      </div>
    );
  }

  // Click — enriched
  if (log.event_type === 'click') {
    return (
      <div className="p-3 bg-blue-50/30 border-l-2 border-blue-200">
        <DetailSection title="Click Details">
          <DetailField label="Element" value={d.element} />
          <DetailField label="Label" value={d.label} />
          <DetailField label="CSS Selector" value={d.cssSelector} mono />
          <DetailField label="Element ID" value={d.id} />
          <DetailField label="Href" value={d.href} mono />
          <DetailField label="Disabled" value={d.disabled ? 'Yes' : null} />
          {d.dataAttributes && Object.keys(d.dataAttributes).length > 0 && (
            <DetailField label="Data Attrs" value={d.dataAttributes} mono />
          )}
        </DetailSection>
      </div>
    );
  }

  // Page View
  if (log.event_type === 'page_view') {
    return (
      <div className="p-3 bg-green-50/30 border-l-2 border-green-200">
        <DetailSection title="Page View">
          <DetailField label="Path" value={d.path} mono />
          <DetailField label="Full URL" value={d.fullUrl} mono />
          <DetailField label="Referrer" value={d.referrer} mono />
          <DetailField label="Title" value={d.title} />
        </DetailSection>
      </div>
    );
  }

  // API Call (success tracking)
  if (log.event_type === 'api_call') {
    return (
      <div className={`p-3 ${d.slow ? 'bg-yellow-50/50 border-l-2 border-yellow-300' : 'bg-gray-50/30 border-l-2 border-gray-200'}`}>
        <DetailSection title={d.slow ? 'Slow API Call' : 'API Call'}>
          <DetailField label="Method" value={d.method} />
          <DetailField label="URL" value={d.fullUrl || d.url} mono copyable />
          <DetailField label="Status" value={`${d.status} ${d.statusText || ''}`} />
          <DetailField label="Duration" value={d.durationMs ? `${d.durationMs}ms` : null} />
          <DetailField label="Response Size" value={d.responseSize ? `~${d.responseSize} bytes` : null} />
        </DetailSection>
      </div>
    );
  }

  // Heartbeat
  if (log.event_type === 'heartbeat') {
    return (
      <div className="p-3 bg-gray-50/20 border-l-2 border-gray-200">
        <DetailSection title="Session Heartbeat">
          <DetailField label="Uptime" value={d.uptime ? `${Math.round(d.uptime / 60)}min` : null} />
          <DetailField label="Tab Visible" value={d.visible ? 'Yes' : 'No'} />
          <DetailField label="Online" value={d.online ? 'Yes' : 'No'} />
          <DetailField label="Queue Size" value={d.queueSize} />
          <DetailField label="Memory" value={d.memoryUsage ? `${Math.round(d.memoryUsage / 1048576)}MB` : null} />
        </DetailSection>
      </div>
    );
  }

  // Page Load Performance
  if (log.event_type === 'page_load') {
    return (
      <div className="p-3 bg-purple-50/30 border-l-2 border-purple-200">
        <DetailSection title="Page Load Performance">
          <DetailField label="TTFB" value={d.ttfbMs ? `${d.ttfbMs}ms` : null} />
          <DetailField label="DOM Interactive" value={d.domInteractiveMs ? `${d.domInteractiveMs}ms` : null} />
          <DetailField label="DOM Complete" value={d.domCompleteMs ? `${d.domCompleteMs}ms` : null} />
          <DetailField label="Load Event" value={d.loadEventMs ? `${d.loadEventMs}ms` : null} />
          <DetailField label="DNS" value={d.dnsMs ? `${d.dnsMs}ms` : null} />
          <DetailField label="Connect" value={d.connectMs ? `${d.connectMs}ms` : null} />
          <DetailField label="Download" value={d.downloadMs ? `${d.downloadMs}ms` : null} />
          <DetailField label="Transfer Size" value={d.transferSize ? `${Math.round(d.transferSize / 1024)}KB` : null} />
        </DetailSection>
      </div>
    );
  }

  // Form Submit
  if (log.event_type === 'form_submit') {
    return (
      <div className="p-3 bg-green-50/30 border-l-2 border-green-200">
        <DetailSection title="Form Submission">
          <DetailField label="Form" value={d.formName} />
          <DetailField label="Fields" value={d.fieldCount} />
          <DetailField label="Filled" value={d.filledFields} />
          <DetailField label="Action" value={d.action} mono />
          <DetailField label="Method" value={d.method} />
        </DetailSection>
      </div>
    );
  }

  // Visibility Change
  if (log.event_type === 'visibility_change') {
    return (
      <div className="p-3 bg-gray-50/20 border-l-2 border-gray-200">
        <DetailSection title="Tab Visibility">
          <DetailField label="State" value={d.state === 'visible' ? 'Tab Focused' : 'Tab Hidden'} />
        </DetailSection>
      </div>
    );
  }

  // Connectivity
  if (log.event_type === 'connectivity') {
    return (
      <div className={`p-3 ${d.status === 'offline' ? 'bg-red-50/30 border-l-2 border-red-200' : 'bg-green-50/30 border-l-2 border-green-200'}`}>
        <DetailSection title="Network Status">
          <DetailField label="Status" value={d.status === 'online' ? 'Back Online' : 'Gone Offline'} />
        </DetailSection>
      </div>
    );
  }

  // Long Task
  if (log.event_type === 'long_task') {
    return (
      <div className="p-3 bg-yellow-50/30 border-l-2 border-yellow-200">
        <DetailSection title="Long Task (UI Jank)">
          <DetailField label="Duration" value={d.durationMs ? `${d.durationMs}ms` : null} />
          <DetailField label="Start Time" value={d.startTime ? `${d.startTime}ms after load` : null} />
          <DetailField label="Name" value={d.name} />
        </DetailSection>
      </div>
    );
  }

  // Scheduler Job Error / Slow
  if (log.event_type === 'scheduler_job_error' || log.event_type === 'scheduler_job_slow' || log.event_type === 'wtf_sync_error') {
    const isError = log.severity === 'error';
    return (
      <div className={`p-3 ${isError ? 'bg-red-50/50 border-l-2 border-red-400' : 'bg-yellow-50/50 border-l-2 border-yellow-400'}`}>
        <DetailSection title={isError ? 'Scheduler Job Failed' : 'Slow Scheduler Job'}>
          <DetailField label="Job ID" value={d.job_id || d.feed_name} mono />
          <DetailField label="Status" value={d.status} />
          <DetailField label="Duration" value={d.duration_ms ? `${d.duration_ms}ms` : null} />
          <DetailField label="Error" value={d.error_message || log.error_message} />
          <DetailField label="Traceback" value={d.traceback || log.traceback} mono copyable />
          <DetailField label="Result" value={d.result_summary} mono />
        </DetailSection>
      </div>
    );
  }

  // Backend Error (generic)
  if (log.log_type === 'backend_error') {
    return (
      <div className="p-3 bg-red-50/50 border-l-2 border-red-400">
        <DetailSection title="Backend Exception">
          <DetailField label="Error" value={log.error_message} />
          <DetailField label="Type" value={log.error_type} mono />
          <DetailField label="Method" value={log.method} />
          <DetailField label="Path" value={log.path} mono />
          <DetailField label="Traceback" value={log.traceback} mono copyable />
        </DetailSection>
      </div>
    );
  }

  // HTTP Request
  if (log.log_type === 'http_request') {
    return (
      <div className="p-3 bg-gray-50/50 border-l-2 border-gray-300">
        <DetailSection title="HTTP Request">
          <DetailField label="Method" value={log.method} />
          <DetailField label="Path" value={log.path} mono />
          <DetailField label="Status" value={log.status_code} />
          <DetailField label="Duration" value={log.duration_ms ? `${log.duration_ms}ms` : null} />
          <DetailField label="User" value={log.user_email} />
          <DetailField label="IP" value={log.ip_address} />
        </DetailSection>
      </div>
    );
  }

  // Business action / generic
  return (
    <div className="p-3 bg-gray-50/50 border-l-2 border-gray-200">
      <DetailSection title="Details">
        <pre className="text-[10px] font-mono bg-white p-2 rounded border max-h-60 overflow-auto whitespace-pre-wrap">
          {JSON.stringify(log.changes || log.details || log, null, 2)}
        </pre>
      </DetailSection>
    </div>
  );
}


// ---- Main Component ----

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [activeTab, setActiveTab] = useState('business_action');
  const [expandedRow, setExpandedRow] = useState(null);
  const limit = 50;

  // Filter state
  const [filters, setFilters] = useState({
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    user_email: '',
    action: '',
    entity_type: '',
    log_type: 'business_action',
  });

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', offset);

      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.user_email) params.append('user_email', filters.user_email);
      if (filters.action) params.append('action', filters.action);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.log_type) params.append('log_type', filters.log_type);

      const response = await axiosInstance.get(`/activity-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, offset]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/activity-logs/stats?hours=24');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchLogs();
      fetchStats();
    }, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs, fetchStats]);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setOffset(0);
    setExpandedRow(null);
    setFilters(prev => ({ ...prev, log_type: tabKey }));
  };

  const handleSearch = () => {
    setOffset(0);
    setExpandedRow(null);
    fetchLogs();
  };

  const toggleRow = (logId) => {
    setExpandedRow(prev => prev === logId ? null : logId);
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    const date = new Date(ts);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Severity indicator
  const getSeverityBadge = (severity) => {
    const styles = {
      error: 'bg-red-100 text-red-800 border-red-200',
      warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      info: 'bg-blue-100 text-blue-800 border-blue-200',
    };
    return styles[severity] || styles.info;
  };

  const getSeverityDot = (severity) => {
    const colors = {
      error: 'bg-red-500',
      warning: 'bg-yellow-500',
      info: 'bg-blue-400',
    };
    return colors[severity] || colors.info;
  };

  // Business action display names
  const getActionLabel = (log) => {
    // Frontend events use event_type
    if (log.log_type === 'frontend_event') {
      const labels = {
        js_error: 'JS Error',
        api_error: 'API Error',
        api_call: 'API Call',
        page_view: 'Page View',
        page_load: 'Page Load',
        click: 'Click',
        form_submit: 'Form Submit',
        unhandled_rejection: 'Promise Rejection',
        react_error: 'React Error',
        console_error: 'Console Error',
        heartbeat: 'Heartbeat',
        visibility_change: 'Tab Focus',
        connectivity: 'Network',
        long_task: 'Long Task',
      };
      return labels[log.event_type] || log.event_type?.replace(/_/g, ' ') || 'Event';
    }
    if (log.log_type === 'backend_error') {
      const backendLabels = {
        scheduler_job_error: 'Scheduler Error',
        scheduler_job_slow: 'Slow Job',
        wtf_sync_error: 'WTF Sync Error',
        unhandled_exception: 'Backend Exception',
      };
      return backendLabels[log.event_type] || log.error_type || 'Backend Error';
    }
    const labels = {
      create_pulse_entry: 'Submitted Pulse',
      verify_tl: 'TL Verified',
      verify_manager: 'Manager Verified',
      verify_bulk: 'Bulk Verified',
      update_commitment: 'Updated Commitment',
      create: 'Created',
      update: 'Updated',
      delete: 'Deleted',
      view: 'Viewed'
    };
    return labels[log.action] || (log.action || '').replace(/_/g, ' ');
  };

  const getActionBadgeColor = (log) => {
    // Frontend event severity-based colors
    if (log.log_type === 'frontend_event' || log.log_type === 'backend_error') {
      return getSeverityBadge(log.severity || 'info');
    }
    const colors = {
      create: 'bg-green-100 text-green-800',
      create_pulse_entry: 'bg-green-100 text-green-800',
      update: 'bg-blue-100 text-blue-800',
      update_commitment: 'bg-blue-100 text-blue-800',
      delete: 'bg-red-100 text-red-800',
      view: 'bg-gray-100 text-gray-800',
      verify_pulse: 'bg-purple-100 text-purple-800',
      verify_tl: 'bg-purple-100 text-purple-800',
      verify_manager: 'bg-purple-100 text-purple-800',
      verify_bulk: 'bg-purple-100 text-purple-800'
    };
    return colors[log.action] || 'bg-gray-100 text-gray-800';
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      manager: 'bg-blue-100 text-blue-800',
      team_leader: 'bg-green-100 text-green-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  // Render details column — compact summary (click row to expand)
  const renderDetailsSummary = (log) => {
    if (log.log_type === 'frontend_event') {
      const d = log.details || {};
      if (log.event_type === 'page_view') {
        return <span className="text-gray-600 font-mono text-xs">{d.path || log.page || '-'}</span>;
      }
      if (log.event_type === 'click') {
        return <span className="text-gray-600 text-xs">{d.label || '-'}</span>;
      }
      if (log.event_type === 'js_error' || log.event_type === 'react_error' || log.event_type === 'unhandled_rejection') {
        return (
          <div className="text-xs">
            <span className="text-red-600 font-medium">{(d.message || '-').substring(0, 120)}</span>
            {d.source && <div className="text-gray-400 truncate text-[10px]">{d.source}:{d.lineno}</div>}
          </div>
        );
      }
      if (log.event_type === 'api_error') {
        return (
          <div className="text-xs">
            <span className="font-mono text-red-600">{d.method} {d.url} </span>
            {d.status ? (
              <Badge variant="destructive" className="text-[9px] px-1 py-0 ml-1">{d.status}</Badge>
            ) : (
              <Badge variant="outline" className="text-[9px] px-1 py-0 ml-1 border-orange-300 text-orange-600">{d.errorCode || 'NO RESPONSE'}</Badge>
            )}
            {d.durationMs && <span className="text-gray-400 ml-1">{d.durationMs}ms</span>}
          </div>
        );
      }
      if (log.event_type === 'console_error') {
        return <span className="text-yellow-700 text-xs">{(d.message || '-').substring(0, 100)}</span>;
      }
      if (log.event_type === 'api_call') {
        return (
          <div className="text-xs flex items-center gap-2">
            <span className="font-mono text-gray-600">{d.method} {d.url}</span>
            <Badge variant="default" className="text-[9px] px-1 py-0">{d.status}</Badge>
            <span className={d.slow ? 'text-yellow-600 font-medium' : 'text-gray-400'}>{d.durationMs}ms{d.slow ? ' SLOW' : ''}</span>
          </div>
        );
      }
      if (log.event_type === 'heartbeat') {
        return <span className="text-gray-400 text-xs">Uptime: {d.uptime ? `${Math.round(d.uptime / 60)}min` : '?'} | {d.visible ? 'Visible' : 'Hidden'} | {d.online ? 'Online' : 'Offline'}</span>;
      }
      if (log.event_type === 'page_load') {
        return <span className="text-purple-600 text-xs">TTFB: {d.ttfbMs}ms | DOM: {d.domCompleteMs}ms | Load: {d.loadEventMs}ms</span>;
      }
      if (log.event_type === 'form_submit') {
        return <span className="text-green-600 text-xs">{d.formName} ({d.filledFields}/{d.fieldCount} fields filled)</span>;
      }
      if (log.event_type === 'visibility_change') {
        return <span className="text-gray-500 text-xs">Tab {d.state === 'visible' ? 'focused' : 'hidden'}</span>;
      }
      if (log.event_type === 'connectivity') {
        return <span className={`text-xs ${d.status === 'offline' ? 'text-red-600' : 'text-green-600'}`}>{d.status === 'online' ? 'Back online' : 'Gone offline'}</span>;
      }
      if (log.event_type === 'long_task') {
        return <span className="text-yellow-600 text-xs">UI blocked for {d.durationMs}ms</span>;
      }
      return <span className="text-gray-400 text-xs">{JSON.stringify(d).substring(0, 100)}</span>;
    }

    if (log.log_type === 'backend_error') {
      const d = log.details || {};
      const isScheduler = log.event_type === 'scheduler_job_error' || log.event_type === 'scheduler_job_slow' || log.event_type === 'wtf_sync_error';
      return (
        <div className="text-xs">
          <span className={`font-medium ${log.severity === 'error' ? 'text-red-600' : 'text-yellow-600'}`}>
            {isScheduler ? (d.job_id || d.feed_name || '') + ': ' : ''}
            {(d.error_message || log.error_message || '-').substring(0, 120)}
          </span>
          <div className="text-gray-400 font-mono">{log.method} {log.path}</div>
          {d.duration_ms && <span className="text-gray-400">{d.duration_ms}ms</span>}
        </div>
      );
    }

    if (log.log_type === 'http_request') {
      return (
        <div className="text-xs flex items-center gap-2">
          <span className="font-mono truncate max-w-[180px]">{log.path}</span>
          <Badge variant={log.status_code < 400 ? 'default' : 'destructive'} className="text-[10px]">
            {log.status_code}
          </Badge>
          <span className={`${log.is_slow ? 'text-yellow-600 font-medium' : 'text-gray-400'}`}>
            {log.duration_ms}ms{log.is_slow ? ' SLOW' : ''}
          </span>
        </div>
      );
    }

    // business_action
    if (log.changes && Object.keys(log.changes).length > 0) {
      return (
        <div className="space-y-1 text-xs">
          {Object.entries(log.changes).slice(0, 3).map(([key, val]) => (
            <div key={key} className="text-gray-600">
              <span className="font-medium">{key}:</span>{' '}
              {typeof val.old === 'object' ? 'modified' : `${val.old || '-'} -> ${val.new || '-'}`}
            </div>
          ))}
          {Object.keys(log.changes).length > 3 && (
            <span className="text-gray-400">+{Object.keys(log.changes).length - 3} more</span>
          )}
        </div>
      );
    }
    if (log.error) {
      return <span className="text-red-500 text-xs">{log.error}</span>;
    }
    return <span className="text-gray-400 text-xs">-</span>;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          Activity Logs
          <HelpTooltip
            tip="Complete audit trail of all actions in the system including frontend errors, API requests, and user activity. Click any row to see full debug details."
            docId="activity-logs-overview"
          />
        </h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh (15s)
            <HelpTooltip
              tip="Logs refresh automatically every 15 seconds."
              docId="activity-logs-overview#realtime"
            />
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchLogs(); fetchStats(); }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Total Activities (24h)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_activities}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                Active Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.by_user?.length || 0}</div>
              <div className="text-xs text-gray-500 mt-1">
                Top: {stats.by_user?.[0]?.user_name || 'N/A'}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Creates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats.by_action?.create || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Errors (24h)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {(stats.by_action?.js_error || 0) + (stats.by_action?.api_error || 0) + (stats.by_action?.react_error || 0) + (stats.by_action?.unhandled_exception || 0)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab Bar */}
      <div className="flex gap-1 border-b">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div>
              <Label htmlFor="start_date" className="flex items-center gap-1">
                Start Date
              </Label>
              <Input
                id="start_date"
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="user_email">User Email</Label>
              <Input
                id="user_email"
                placeholder="Search by email..."
                value={filters.user_email}
                onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
              />
            </div>
            <div>
              <Label>Action</Label>
              <Select
                value={filters.action}
                onValueChange={(v) => setFilters({ ...filters, action: v === 'all' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="create_pulse_entry">Submitted Pulse</SelectItem>
                  <SelectItem value="verify_tl">TL Verified</SelectItem>
                  <SelectItem value="verify_manager">Manager Verified</SelectItem>
                  <SelectItem value="verify_bulk">Bulk Verified</SelectItem>
                  <SelectItem value="update_commitment">Updated Commitment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Entity Type</Label>
              <Select
                value={filters.entity_type}
                onValueChange={(v) => setFilters({ ...filters, entity_type: v === 'all' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="pulse_entry">Pulse Entries</SelectItem>
                  <SelectItem value="commitment">Commitments</SelectItem>
                  <SelectItem value="gym">Gyms</SelectItem>
                  <SelectItem value="user">Users</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={handleSearch} className="w-full">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-sm font-medium">
              Activity Log ({total} total)
            </CardTitle>
            <span className="text-[10px] text-gray-400">Click any row for full debug details</span>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No activity logs found</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[28px]"></TableHead>
                    <TableHead className="w-[28px]"></TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <React.Fragment key={log.id}>
                      <TableRow
                        className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                          log.severity === 'error' ? 'bg-red-50/50' : ''
                        } ${expandedRow === log.id ? 'bg-blue-50/30' : ''}`}
                        onClick={() => toggleRow(log.id)}
                      >
                        <TableCell className="px-1">
                          {expandedRow === log.id
                            ? <ChevronDown className="w-3.5 h-3.5 text-blue-500" />
                            : <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                          }
                        </TableCell>
                        <TableCell className="px-1">
                          <div className={`w-2 h-2 rounded-full ${getSeverityDot(log.severity || (log.log_type === 'backend_error' ? 'error' : 'info'))}`} />
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {formatTimestamp(log.timestamp)}
                        </TableCell>
                        <TableCell className="text-xs">
                          <div>{log.user_name || log.user_email || 'System'}</div>
                          {log.user_role && (
                            <Badge variant="outline" className={`text-[10px] mt-0.5 ${getRoleBadge(log.user_role)}`}>
                              {log.user_role}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px] bg-gray-50">
                            {log.log_type === 'frontend_event' ? 'Frontend' :
                             log.log_type === 'backend_error' ? 'Backend' :
                             log.log_type === 'http_request' ? 'HTTP' :
                             'Action'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getActionBadgeColor(log)}>
                            {getActionLabel(log)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs max-w-[400px]">
                          {renderDetailsSummary(log)}
                        </TableCell>
                      </TableRow>
                      {expandedRow === log.id && (
                        <TableRow>
                          <TableCell colSpan={7} className="p-0">
                            <ExpandedDetails log={log} />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex justify-between items-center mt-4">
                <div className="text-sm text-gray-500">
                  Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOffset(offset + limit)}
                    disabled={offset + limit >= total}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityLogs;
